import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { Clock, Activity, GitBranch, AlertTriangle, Timer, Gauge } from "lucide-react";
import { StatCard } from "./ui";

const PIE_COLORS = [
  "hsl(202,46%,30%)", "hsl(42,63%,52%)", "hsl(6,55%,49%)",
  "hsl(168,34%,34%)", "hsl(77,8%,42%)", "hsl(202,38%,46%)",
];

const tooltipStyle = {
  background: "hsl(210,15%,10%)", border: "none", color: "#fff",
  fontFamily: "JetBrains Mono", fontSize: 12, borderRadius: 0,
};

export default function Overview({ metrics, onGoRepos }) {
  if (!metrics) return null;

  if (metrics.total_signals === 0) {
    return (
      <div className="border-2 border-dashed border-foreground bg-card p-12 text-center">
        <Activity size={32} className="text-primary mx-auto mb-4" />
        <h3 className="font-display font-extrabold tracking-tight text-2xl">No telemetry yet</h3>
        <p className="font-mono text-sm text-muted-foreground mt-2 max-w-md mx-auto">
          Add a public GitHub repository to start intercepting live developer friction signals.
        </p>
        <button onClick={onGoRepos} data-testid="overview-add-repo-cta" className="btn btn-primary mt-6">
          Track a repository
        </button>
      </div>
    );
  }

  const cats = metrics.category_breakdown || [];
  const hoursByRepo = metrics.hours_by_repo || [];
  const timeline = metrics.timeline || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        <div className="bg-background"><StatCard icon={Clock} label="Dev-hours lost" value={`${metrics.developer_hours_lost}h`} sub="estimated, all repos" accent /></div>
        <div className="bg-background"><StatCard icon={Gauge} label="Interception rate" value={`${metrics.friction_interception_rate}%`} sub="signals auto-categorized" /></div>
        <div className="bg-background"><StatCard icon={Timer} label="Avg time-to-value" value={`${metrics.avg_time_to_value_minutes}m`} sub="signal → routed fix" /></div>
        <div className="bg-background"><StatCard icon={AlertTriangle} label="Open signals" value={metrics.open_signals} sub={`${metrics.watched_repos} repos watched`} /></div>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 bg-card border border-border p-5">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Estimated developer-hours lost by repository</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={hoursByRepo} margin={{ left: -18, right: 8 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="hsl(48,18%,82%)" vertical={false} />
              <XAxis dataKey="repo" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "hsl(77,6%,40%)" }}
                tickFormatter={(v) => v.split("/")[1] || v} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontFamily: "JetBrains Mono", fontSize: 11, fill: "hsl(77,6%,40%)" }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(48,17%,86%)" }} />
              <Bar dataKey="hours" fill="hsl(202,46%,30%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-5 bg-card border border-border p-5">
          <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Friction by category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={cats} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={92} innerRadius={48}>
                {cats.map((c, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="hsl(210,15%,8%)" strokeWidth={1} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {cats.slice(0, 5).map((c, i) => (
              <div key={i} className="flex items-center justify-between font-mono text-xs">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{c.category}</span>
                <span className="text-muted-foreground">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border p-5">
        <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Signals intercepted over time (by issue date)</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={timeline} margin={{ left: -22, right: 8 }}>
            <defs>
              <linearGradient id="sigGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(6,55%,49%)" stopOpacity={0.4} />
                <stop offset="100%" stopColor="hsl(6,55%,49%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 2" stroke="hsl(48,18%,82%)" vertical={false} />
            <XAxis dataKey="date" tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "hsl(77,6%,40%)" }} interval="preserveStartEnd" />
            <YAxis tick={{ fontFamily: "JetBrains Mono", fontSize: 11, fill: "hsl(77,6%,40%)" }} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="signals" stroke="hsl(6,55%,49%)" strokeWidth={2} fill="url(#sigGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
